import * as React from 'react'
import { CSSProperties, ReactElement, useEffect, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import { Form, Row } from 'antd'
import { isBrowser, isTablet } from 'react-device-detect'
import styled from 'styled-components'

import { getTimeOptions, onSubmit, setNextReviewDateByOptions } from '../utils'
import { Icon, LabelIcon, NextReviewField, PageHeader } from '@lmc/ui-kit'
import { Button, Heading, Text } from '@lmc/ui-kit/src/general'
import { colors } from '@lmc/common/constants'
import {
  CarePlanLinkingConfirmationModal,
  ConsentFormTextBox,
  LogsSection,
  NextReviewCard
} from './components'
import PDFHeader from './PDFScreen/PDFHeader'

import { history } from '../../../../../store'
import { ConsentForm, Home, LogCategoryItem, Resident } from '@lmc/ts-common'
import { useNavigate } from 'react-router-dom'
import { CarePlanSelector } from 'components/generic'
import SignatureModal from 'components/generic/SignatureSection/SignatureModal'
import { CarePlanAdlExtended } from 'components/generic/CarePlanSelector/CarePlanSelector'
import {
  ConsentConfirmationCard,
  getIsConsentConfirmDisabled,
  getNextReviewFieldState
} from '../ResidentConsentFormsScreen/components'
import { ConsentStatus, getIsSignStatus } from '@lmc/common/types'
import ConsentStatusTag from 'screens/CarePlans/screens/CarePlanReviewScreen/components/ConsentSection/ConsentStatusTag'
import PdfExport from 'components/pdf/PdfExport'
import { NAMESPACE } from 'locale'
import { consentConstants } from 'locale/consent'

type CarePlanItem = CarePlanAdlExtended | string

const extractCarePlanIds = (carePlans: CarePlanItem[]): string[] =>
  carePlans.map((cp) => (typeof cp === 'string' ? cp : cp.id))

type ConsentFormScreenProps = {
  fetchResidentConsentFormByID: (id: string) => void
  consentForm: ConsentForm
  home: Home
  printPDF?: boolean
  resident: Resident
  router: any
  logs: any
  signatureItem: LogCategoryItem
  updateConsent: ({
    isRefused,
    comment,
    editedBy,
    editedOn,
    nextReviewDate,
    consentForm
  }: any) => Promise<any>
}

export const ConsentFormScreen = ({
  consentForm,
  fetchResidentConsentFormByID,
  home,
  printPDF,
  logs,
  resident,
  router,
  signatureItem,
  updateConsent
}: ConsentFormScreenProps): ReactElement | null => {
  const { t } = useTranslation()
  const translate = (key: string, variable?: any) =>
    t(`consent.createConsentForms.components.consentModal.${key}`, variable)

  const labelTextBoxTranslate = (key: string, variable?: any) =>
    t(`components.labelTextBox.${key}`, variable)

  const STATUS_TO_OPTION_KEY: Record<ConsentStatus, string> = {
    [ConsentStatus.noStatus]: 'OPTION_NO_STATUS',
    [ConsentStatus.consentNotObtained]: 'OPTION_CONSENT_NOT_OBTAINED',
    [ConsentStatus.completed]: 'OPTION_COMPLETED',
    [ConsentStatus.planned]: 'OPTION_PLANNED',
    [ConsentStatus.requested]: 'OPTION_REQUESTED',
    [ConsentStatus.scheduled]: 'OPTION_SCHEDULED',
    [ConsentStatus.postponed]: 'OPTION_POSTPONED',
    [ConsentStatus.cancelled]: 'OPTION_CANCELLED',
    [ConsentStatus.withdrawn]: 'OPTION_WITHDRAWN',
    [ConsentStatus.refused]: 'OPTION_REFUSED',
    [ConsentStatus.provided]: 'OPTION_PROVIDED',
    [ConsentStatus.pending]: 'OPTION_PLANNED'
  }

  const getStatusLabel = (status: ConsentStatus): string => {
    const optionKey = STATUS_TO_OPTION_KEY[status]
    return optionKey ? t(`consent:${optionKey}`) : status
  }

  const [isReviewing, setIsReviewing] = useState<boolean>(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [nextReviewDate, setNextReviewDate] = useState<string | undefined>()
  const [additionalInfo, setAdditionalInfo] = useState<string | undefined>()
  const [timeOptionNextReview, setTimeOptionNextReview] = useState<
    string | undefined
  >()
  const [selectedCarePlans, setSelectedCarePlans] = useState<
    CarePlanAdlExtended[] | string[]
  >([])
  const [initialCarePlanIds, setInitialCarePlanIds] = useState<string[]>([])
  const [showSignatureModal, setShowSignatureModal] = useState(false)
  const [showCarePlanLinkingModal, setShowCarePlanLinkingModal] =
    useState(false)
  const [newlyLinkedCarePlans, setNewlyLinkedCarePlans] = useState<
    CarePlanAdlExtended[]
  >([])

  const navigate = useNavigate()

  const [form] = Form.useForm()

  const consentFormIdFromURL = router.params.consent_form_id

  const [consentStatus, setConsentStatus] = useState<{
    status: ConsentStatus
    reason?: string
    lastReviewDate?: string | null
  }>({
    status: ConsentStatus.noStatus
  })
  const isSignStatus = getIsSignStatus(consentStatus.status)

  const isInitialized = useRef(false)

  useEffect(() => {
    fetchResidentConsentFormByID(consentFormIdFromURL)
  }, [consentFormIdFromURL])

  useEffect(() => {
    // @ts-ignore
    setNextReviewDateByOptions(timeOptionNextReview, setNextReviewDate, form)
  }, [timeOptionNextReview])

  useEffect(() => {
    if (consentForm && !isInitialized.current) {
      isInitialized.current = true

      // Initialize selectedCarePlans from consentForm and save initial IDs
      if (consentForm.carePlans?.length) {
        setSelectedCarePlans(consentForm.carePlans)
        // Store initial care plan IDs to detect newly linked plans later
        const initialIds = consentForm.carePlans.map(
          (cp: string | CarePlanAdlExtended) =>
            typeof cp === 'string' ? cp : cp.id
        )
        setInitialCarePlanIds(initialIds)
      }

      // Initialize consentStatus from consentForm
      if (consentForm.consentStatus) {
        setConsentStatus({
          status: consentForm.consentStatus,
          reason: consentForm.statusReason,
          lastReviewDate: consentForm.lastReviewed as unknown as string
        })
      }
      if (consentForm.nextReviewDate) {
        setNextReviewDate(
          dayjs(consentForm.nextReviewDate) as unknown as string
        )
      }
    }
  }, [consentForm])

  const onSubmitSign = async ({ formState, onSuccess }: any) => {
    if (isSubmitting) return
    setIsSubmitting(true)
    await onSubmit({
      resident,
      nextReviewDate,
      formState,
      consentSingleForm: consentForm,
      isSingleSign: true,
      item: signatureItem.id,
      category: signatureItem.category
    })
    setIsSubmitting(false)
    onSuccess()
    fetchResidentConsentFormByID(consentFormIdFromURL)
  }

  const handleSave = async () => {
    try {
      const res = await updateConsent({
        isRefused: false,
        comment: additionalInfo,
        editedBy: resident.id,
        editedOn: dayjs(new Date()).toISOString(),
        nextReviewDate: nextReviewDate
          ? dayjs(nextReviewDate).toISOString()
          : null,
        consentForm,
        consentStatus: consentStatus.status,
        carePlans: extractCarePlanIds(selectedCarePlans),
        statusReason: consentStatus.reason,
        lastReviewed: consentStatus.lastReviewDate
      })
      if (res) {
        // Filter to only newly linked care plans (not in initial set)
        const newlyLinked = (selectedCarePlans as CarePlanAdlExtended[]).filter(
          (cp) => !initialCarePlanIds.includes(cp.id)
        )

        if (newlyLinked.length > 0) {
          setNewlyLinkedCarePlans(newlyLinked)
          setShowCarePlanLinkingModal(true)
        } else {
          handleCancel()
        }
      }
    } catch (e) {
      console.log(e)
    }
  }

  const handleCancel = () => {
    setIsReviewing(false)
    setAdditionalInfo(undefined)
    setNewlyLinkedCarePlans([])
  }

  const handleDateChange = (date: any, _dateString: any) => {
    if (date !== null) {
      setNextReviewDate(date)
    } else {
      setTimeOptionNextReview(undefined)
      setNextReviewDate(undefined)
    }
  }

  const isDesktop = isBrowser || isTablet

  const nextReviewFieldState = getNextReviewFieldState(consentStatus.status)

  useEffect(() => {
    if (nextReviewFieldState.isDisabled && isReviewing) {
      setNextReviewDate(undefined)
      setTimeOptionNextReview(undefined)
      form.setFieldValue('nextReviewDate', undefined)
    }
  }, [nextReviewFieldState.isDisabled, isReviewing, form])

  if (!consentForm || !resident) return null

  const isNextReviewDateInvalid =
    nextReviewFieldState.isRequired && !nextReviewDate
  const isConfirmDisabled = getIsConsentConfirmDisabled({
    ...consentStatus,
    disabled: isNextReviewDateInvalid
  })

  const pdfUrl = `residents/${resident?.id}/pdf/consent/${consentForm?.id}`
  const url = `/api/services/pdf/export?url=${encodeURIComponent(pdfUrl)}`

  return (
    <div style={printPDF ? styles.pdfWrapper : styles.wrapper}>
      <Row style={styles.mainRow as CSSProperties}>
        <Row>{printPDF && <PDFHeader home={home} resident={resident} />}</Row>
        <Row>
          <div style={styles.header}>
            <PageHeader
              sticky
              // @ts-ignore
              backLink={!printPDF}
              onBack={() => navigate('../', { relative: 'path' })}
              // @ts-ignore
              rightChildren={
                !printPDF && (
                  <PdfExport
                    pdfTitle={`${resident?.name?.first} ${resident?.name?.last}'s ${consentForm?.title}`}
                    url={url}
                    text={
                      isDesktop
                        ? translate('downloadPDF')
                        : translate('downloadPDFMobile')
                    }
                  />
                )
              }
              children={
                <TitleWrapper>
                  <HeadingStyled level={1}> {consentForm?.title}</HeadingStyled>{' '}
                  <ButtonsWrapper>
                    {consentStatus.status !== ConsentStatus.noStatus && (
                      <ConsentStatusTag
                        status={consentStatus.status}
                        label={getStatusLabel(consentStatus.status)}
                      />
                    )}
                    {!isReviewing ? (
                      <Button
                        variant="primary"
                        onClick={() => setIsReviewing(true)}
                      >
                        {t(`general.buttons.review`)}
                      </Button>
                    ) : (
                      <InReviewControllsWrapper>
                        <Button
                          variant="default"
                          style={{ marginRight: 10 }}
                          onClick={() => handleCancel()}
                        >
                          {t(`general.buttons.cancel`)}
                        </Button>
                        <Button
                          disabled={isConfirmDisabled}
                          onClick={() =>
                            isSignStatus
                              ? setShowSignatureModal(true)
                              : handleSave()
                          }
                          variant="primary"
                          style={{ marginRight: 10 }}
                          icon={<Icon name="CheckOutlined" />}
                        >
                          {isSignStatus ? t('sign') : t(`general.buttons.save`)}
                        </Button>
                      </InReviewControllsWrapper>
                    )}
                  </ButtonsWrapper>
                </TitleWrapper>
              }
              onClick={history.back}
            />
          </div>
          <FullWidthContainer>
            <CarePlanSelector
              residentId={resident?.id}
              selectedCarePlans={selectedCarePlans}
              onChange={setSelectedCarePlans}
              readonly={!isReviewing}
              label={
                <LabelContainer>
                  <Text level={1} weight="bold">
                    {translate('linkedCarePlans')}
                  </Text>
                  <Text level={2}>{translate('consentFormsWillAppear')}</Text>
                </LabelContainer>
              }
            />
          </FullWidthContainer>
          <FullWidthContainer>
            <Mb24></Mb24>
          </FullWidthContainer>

          {isReviewing && (
            <FullWidthContainer>
              <ConsentConfirmationCard
                onStatusChange={setConsentStatus}
                selectedStatus={consentStatus}
              />
              <Mb24 />
              <Form form={form}>
                <NextReviewField
                  disabled={nextReviewFieldState.isDisabled}
                  localisation={{
                    title: 'Next review',
                    additionalInfo: 'Any additional comments on this review?'
                  }}
                  dateValue={(nextReviewDate as string) || undefined}
                  datePickerOnChange={handleDateChange}
                  radioGroupOnChange={setTimeOptionNextReview}
                  timeValue={timeOptionNextReview}
                  timeOptions={getTimeOptions(translate)}
                  isMobile={!isDesktop}
                />
              </Form>
            </FullWidthContainer>
          )}

          <ConsentFormTextBox
            styles={{ marginTop: 0 }}
            consentForm={consentForm}
            localisation={{
              title: consentForm?.title || '',
              subTitle: translate('statement', {
                variable: resident?.preferredName || resident?.name?.first
              }),
              readOnly: labelTextBoxTranslate('readOnly')
            }}
            resident={resident}
          />
        </Row>
        {!isReviewing && (
          <NextReviewCard
            nextReviewDate={
              (consentForm?.nextReviewDate || nextReviewDate) as string
            }
            style={styles.reviewCard}
            translate={translate}
          />
        )}

        <LogsSection
          consentForm={consentForm}
          isSubmitting={isSubmitting}
          logs={logs}
          resident={resident}
          translate={translate}
        />
        <br />
      </Row>
      {!printPDF && (
        <Row>
          <LabelIcon
            iconColor={colors.pink}
            iconName="QuestionCircleOutlined"
            onPress={() =>
              window.open(
                'http://support.logmycare.co.uk/en/articles/6014582-how-do-i-review-a-consent-or-lack-of-capacity-form',
                '_blank'
              )
            }
            text="Help"
            textColor={colors.pink}
          />
        </Row>
      )}
      <SignatureModal
        resident={resident}
        footerLabel={consentForm?.title}
        confirmText={translate('confirm', {
          variable: resident?.preferredName || resident?.name?.first
        })}
        isShowing={showSignatureModal}
        isSubmitting={isSubmitting}
        onSubmit={({ formState }: any) => {
          onSubmitSign({
            formState,
            onSuccess: () => {
              setShowSignatureModal(false)
              handleSave()
            }
          })
        }}
        onClose={() => setShowSignatureModal(false)}
      />
      <CarePlanLinkingConfirmationModal
        isModalVisible={showCarePlanLinkingModal}
        setIsModalVisible={setShowCarePlanLinkingModal}
        newlyLinkedCarePlans={newlyLinkedCarePlans}
        onConfirm={handleCancel}
      />
    </div>
  )
}

const styles = {
  header: {
    width: '100%',
    position: 'sticky',
    top: 0,
    zIndex: 10
  } as React.CSSProperties,
  mainRow: {
    flexDirection: 'column'
  },
  reviewCard: {
    marginTop: 24
  },
  wrapper: {
    maxWidth: 1080,
    padding: '0px 0px 100px',
    marginLeft: 'auto',
    marginRight: 'auto'
  },
  pdfWrapper: {
    position: 'absolute' as any,
    top: 0,
    left: 0,
    width: 1190,
    minHeight: 1480,
    zIndex: 1000,
    background: 'white',
    padding: '24px 28px'
  }
}

const FullWidthContainer = styled.div`
  width: 100%;
`

const LabelContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`

const Mb24 = styled.div`
  margin-bottom: 24px;
`

const HeadingStyled = styled(Heading)`
  color: ${(props) => props.theme.color.neutral.color3} !important;
  margin-right: 20px;
  margin-left: 16px;
`

const TitleWrapper = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
`

const InReviewControllsWrapper = styled.div`
  display: flex;
`

const ButtonsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`
